import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import Box from '@material-ui/core/Box';
import {
    Button,
    Dialog,
    Grid,
    makeStyles,
    TextField,
    Theme,
    Typography,
    withStyles,
} from '@material-ui/core';
import Spin from '../../components/atoms/Spin/Spin';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TablePagination from '../../components/atoms/TablePagination/TablePagination';
import { fetchPaymentDetails } from '../../api/payment-details';
import { Search } from '@material-ui/icons';
import DialogTitle from '@material-ui/core/DialogTitle';
import ImageGallery from '../../components/molecules/ImageGallery/ImageGallery';
import CloseIcon from '@material-ui/icons/Close';

export const useStyles = makeStyles((theme: Theme) => ({
    formControl: {
        minWidth: 120,
        width: '100%',
    },
    checkbox: {
        padding: 0,
        margin: '0 8px 0 0',
    },
    dialogPaper: {
        minHeight: '55vh',
    },
    buttons: {
        '& button': {
            marginRight: theme.spacing(1),
            '&:last-child': {
                marginRight: 0,
            },
        },
    },
    btn: {
        marginTop: theme.spacing(2),
    },
    actionButton: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        position: 'relative',
        transition: 'all .3s ease-in',
    },
    filterRow: {
        marginTop: theme.spacing(1),
    },
    tableRow: {
        cursor: 'pointer',
    },
    dialogTitle: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    closeIcon: { cursor: 'pointer' },
}));

const StyledCell = withStyles(() => ({
    root: {
        borderBottom: '1px solid #b3b3b3',
    },
}))(TableCell);

const PaymentDetails = () => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [iin, setIIN] = useState<number | null>(null);
    const [amount, setAmount] = useState<number | null>(null);
    const [isPaymentSignDialogOpen, setPaymentSignDialogOpen] = useState(false);
    const [paymentSignInfo, setPaymentSignInfo] = useState<{
        id: number;
        desc?: string;
    }>({
        id: 0,
        desc: '',
    });
    // Pagination
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [total, setTotal] = useState(0);

    // Fetch
    const fetch = useCallback(async () => {
        setLoading(true);
        try {
            const x = await fetchPaymentDetails({
                page,
                size: perPage,
                iin: iin || null,
                amount,
            });
            setItems(x.content);
            setTotal(x.total_elements);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных: ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [page, perPage, amount, iin, enqueueSnackbar]);

    const handleIINChange = (e: any) => {
        setIIN(e.target.value);
    };

    const handleAmountChange = (e: any) => {
        setAmount(e.target.value);
    };

    const handleSearch = () => {
        fetch();
    };

    const handleEnterSearch = (e: any) => {
        if (e.key === 'Enter') {
            fetch();
        }
    };

    const handlePaymentSignDialogClose = useCallback(() => {
        setPaymentSignDialogOpen(!isPaymentSignDialogOpen);
    }, [isPaymentSignDialogOpen]);

    const handlePaymentSignClick = (item: any) => {
        setPaymentSignInfo({ id: item.asset_id, desc: item.date_created.toString() });
        setPaymentSignDialogOpen(true);
    };

    useEffect(() => {
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage]);

    // @ts-ignore
    return (
        <Box position={'relative'}>
            <Grid container spacing={3}>
                <Grid item xs={6} md={4} xl={4} lg={4}>
                    <TextField
                        label="Введите ИИН"
                        fullWidth
                        type={'number'}
                        value={iin}
                        variant="outlined"
                        className={classes.formControl}
                        onChange={handleIINChange}
                        onKeyDown={handleEnterSearch}
                    />
                </Grid>
                <Grid item xs={6} md={4} xl={4} lg={4}>
                    <TextField
                        label="Введите сумму"
                        fullWidth
                        value={amount}
                        type={'number'}
                        variant="outlined"
                        className={classes.formControl}
                        onChange={handleAmountChange}
                        onKeyDown={handleEnterSearch}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                <Grid item xs={3} className={classes.buttons}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                    >
                        Поиск
                    </Button>
                </Grid>
            </Grid>
            <Box position={'relative'} overflow="auto">
                <Box display="flex">
                    <Box flex={4}>
                        {loading && <Spin variant="inline" />}
                        <Box position={'relative'}>
                            {loading && <Spin />}
                            <TableContainer>
                                <Table size={'small'} aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Наименование платежа</TableCell>
                                            <TableCell>Дата создания</TableCell>
                                            <TableCell>Сумма</TableCell>
                                            <TableCell>Комиссия</TableCell>
                                            <TableCell>№ чека</TableCell>
                                            <TableCell>ИИН плательщика</TableCell>
                                            <TableCell>ФИО плательщика</TableCell>
                                            <TableCell>№ телефона плательщика</TableCell>
                                            <TableCell>Статус</TableCell>
                                            <TableCell>Подпись</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item, index) => {
                                            return (
                                                <TableRow
                                                    className={classes.tableRow}
                                                    key={index}
                                                    hover
                                                >
                                                    <StyledCell>{item.payment_assign}</StyledCell>
                                                    <StyledCell>{item.date_created}</StyledCell>
                                                    <StyledCell>{item.amount}</StyledCell>
                                                    <StyledCell>{item.commission}</StyledCell>
                                                    <StyledCell>{item.check_number}</StyledCell>
                                                    <StyledCell>{item.iin_payer}</StyledCell>
                                                    <StyledCell>{item.payer_full_name}</StyledCell>
                                                    <StyledCell>
                                                        {item.payer_mobile_phone}
                                                    </StyledCell>
                                                    <StyledCell>{item.status}</StyledCell>
                                                    <StyledCell>
                                                        <Button
                                                            variant="contained"
                                                            onClick={() =>
                                                                handlePaymentSignClick(item)
                                                            }
                                                        >
                                                            Отобразить
                                                        </Button>
                                                    </StyledCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                </Box>
            </Box>
            {isPaymentSignDialogOpen && paymentSignInfo.id && (
                <Dialog
                    classes={{ paper: classes.dialogPaper }}
                    open={isPaymentSignDialogOpen}
                    fullWidth={true}
                    maxWidth="sm"
                    onClose={handlePaymentSignDialogClose}
                >
                    <DialogTitle disableTypography className={classes.dialogTitle}>
                        <Typography variant={'h3'}>Подпись плательщика</Typography>
                        <CloseIcon
                            className={classes.closeIcon}
                            onClick={handlePaymentSignDialogClose}
                        />
                    </DialogTitle>
                    <Box>
                        <Box
                            marginTop={3}
                            margin={3}
                            css={{ display: 'flex', justifyContent: 'center' }}
                        >
                            <ImageGallery photos={[paymentSignInfo]} />
                        </Box>
                    </Box>
                </Dialog>
            )}

            <TablePagination
                total={total}
                perPage={perPage}
                page={page}
                onChangePage={setPage}
                onChangeRowsPerPage={setPerPage}
            />
        </Box>
    );
};

export default PaymentDetails;
